import { Flow } from "../types";
import { SCREENS } from "../screens";
import { Contract, ProcessSpecification } from "@/types/contract";
import { isCustomerIdentified, psRequestWelcomeNext, skipToRecapitulation } from "../helpers";
import { isPSPaperRequest, shouldCollectGDPRAgreements, skipMeterReadings } from "@/utils/contract";
import { isHousehold } from "@/utils/customer";

export const PS_PROCESS_CONTRACT: Flow = {
  [SCREENS.WELCOME]: {
    back: () => null,
    next: (contract: Contract) => {
      if (isCustomerIdentified(contract)) {
        return SCREENS.CUSTOMER;
      }
      return SCREENS.IDENTIFICATION;
    },
  },
  [SCREENS.IDENTIFICATION]: {
    back: (contract: Contract) =>
      contract.processSpecification === ProcessSpecification.ONLINE ? SCREENS.SUPPLY_POINT_CONFIRM : SCREENS.WELCOME,
    next: () => SCREENS.CUSTOMER,
  },
  [SCREENS.CUSTOMER]: {
    back: (contract: Contract) =>
      contract.processSpecification === ProcessSpecification.ADHOC ? SCREENS.WELCOME : SCREENS.IDENTIFICATION,
    next: (contract: Contract) =>
      contract.processSpecification === ProcessSpecification.ONLINE
        ? SCREENS.SUPPLY_POINT
        : contract.supplyPoint.depositMethod !== 6 && contract.supplyPoint.depositMethod !== 8
        ? SCREENS.PAYMENTS
        : SCREENS.RECAPITULATION,
  },
  [SCREENS.SUPPLY_POINT]: {
    back: () => SCREENS.CUSTOMER,
    next: () => SCREENS.TARIFF,
  },
  [SCREENS.TARIFF]: {
    back: () => SCREENS.SUPPLY_POINT,
    next: () => SCREENS.PAYMENTS,
  },
  [SCREENS.PAYMENTS]: {
    back: (contract: Contract) =>
      contract.processSpecification === ProcessSpecification.ADHOC ? SCREENS.CUSTOMER : SCREENS.TARIFF,
    next: () => SCREENS.RECAPITULATION,
  },
  [SCREENS.RECAPITULATION]: {
    back: () => SCREENS.PAYMENTS,
    next: (contract: Contract, options?: { gdprAgreed?: boolean; isContractRequest?: boolean }) =>
      !options?.gdprAgreed && shouldCollectGDPRAgreements(contract, options?.isContractRequest ?? false)
        ? SCREENS.AGREEMENTS
        : SCREENS.VIEW,
  },
  [SCREENS.AGREEMENTS]: {
    back: () => SCREENS.RECAPITULATION,
    next: () => SCREENS.VIEW,
  },
  [SCREENS.VIEW]: {
    back: () => SCREENS.RECAPITULATION,
    next: () => SCREENS.SIGNATURE,
  },
  [SCREENS.SIGNATURE]: {
    back: () => SCREENS.VIEW,
    next: () => SCREENS.SMS_CODE,
  },
  [SCREENS.SIGNATURE_ON_PHONE]: {
    back: () => null,
    next: () => SCREENS.THANK_YOU,
  },
  [SCREENS.SMS_CODE]: {
    back: () => SCREENS.SIGNATURE,
    next: () => SCREENS.THANK_YOU,
  },
  [SCREENS.THANK_YOU]: {
    back: () => null,
    next: () => null,
  },
};

export const PS_PROCESS_REQUEST: Flow = {
  [SCREENS.WELCOME]: {
    back: () => null,
    next: psRequestWelcomeNext,
  },
  [SCREENS.REQUEST_NEW_WELCOME]: {
    back: () => null,
    next: psRequestWelcomeNext,
  },
  [SCREENS.IDENTIFICATION]: {
    back: () => SCREENS.WELCOME,
    next: (contract: Contract) =>
      skipToRecapitulation(contract, new URLSearchParams(document.location.search).get("process_reason"))
        ? SCREENS.RECAPITULATION
        : SCREENS.CUSTOMER,
  },
  [SCREENS.SUPPLY_POINT_CONFIRM]: {
    back: () => {
      const params = new URLSearchParams(document.location.search);
      return `${SCREENS.WELCOME}?${params.toString()}`;
    },
    next: () => SCREENS.CUSTOMER,
  },
  [SCREENS.CUSTOMER]: {
    back: () => SCREENS.WELCOME,
    next: (contract: Contract) => (skipMeterReadings(contract) ? SCREENS.NEW_CUSTOMER : SCREENS.SUPPLY_POINT),
  },
  [SCREENS.SUPPLY_POINT]: {
    back: () => SCREENS.CUSTOMER,
    next: () => SCREENS.NEW_CUSTOMER,
  },
  [SCREENS.NEW_CUSTOMER]: {
    back: (contract: Contract) => (skipMeterReadings(contract) ? SCREENS.CUSTOMER : SCREENS.SUPPLY_POINT),
    next: (contract: Contract) =>
      contract.payments.length > 0 && isHousehold(contract.customer) ? SCREENS.CLAIM_PAYMENT : SCREENS.RECAPITULATION,
  },
  [SCREENS.CLAIM_PAYMENT]: {
    back: () => SCREENS.NEW_CUSTOMER,
    next: () => SCREENS.RECAPITULATION,
  },
  [SCREENS.RECAPITULATION]: {
    back: () => SCREENS.NEW_CUSTOMER,
    next: (contract: Contract, options?: { gdprAgreed?: boolean; isContractRequest?: boolean }) =>
      contract.processReason === "02"
        ? SCREENS.THANK_YOU
        : !options?.gdprAgreed && shouldCollectGDPRAgreements(contract, options?.isContractRequest ?? false)
        ? SCREENS.AGREEMENTS
        : isPSPaperRequest(contract)
        ? SCREENS.THANK_YOU
        : SCREENS.VIEW,
  },
  [SCREENS.AGREEMENTS]: {
    back: () => SCREENS.RECAPITULATION,
    next: (contract: Contract) => (isPSPaperRequest(contract) ? SCREENS.THANK_YOU : SCREENS.VIEW),
  },
  [SCREENS.VIEW]: {
    back: () => SCREENS.RECAPITULATION,
    next: () => SCREENS.SIGNATURE,
  },
  [SCREENS.SIGNATURE]: {
    back: () => SCREENS.VIEW,
    next: () => SCREENS.SMS_CODE,
  },
  [SCREENS.SIGNATURE_ON_PHONE]: {
    back: () => null,
    next: () => SCREENS.THANK_YOU,
  },
  [SCREENS.SMS_CODE]: {
    back: () => SCREENS.SIGNATURE,
    next: () => SCREENS.THANK_YOU,
  },
  [SCREENS.THANK_YOU]: {
    back: () => null,
    next: () => null,
  },
};
